import { useState, useEffect } from 'react';
import { Button } from '../ui';
import {
  CookiePreferences,
  COOKIE_CONSENT_KEY,
  COOKIE_PREFERENCES_KEY,
  defaultPreferences,
  hasConsented,
} from './cookieUtils';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    if (!hasConsented()) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setIsVisible(false);
    window.dispatchEvent(
      new CustomEvent('cookieConsentUpdated', { detail: prefs })
    );
  };

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const acceptSelected = () => {
    saveConsent(preferences);
  };

  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setShowPreferences(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || e.key === 'Enter')
            setShowPreferences(false);
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close cookie preferences"
      />
      <div className="relative w-full max-w-lg rounded-xl bg-slate-800 border border-slate-700 shadow-2xl">
        <div className="p-6">
          {!showPreferences ? (
            <>
              <div className="flex items-start gap-4">
                <span className="text-3xl">🍪</span>
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">
                    Cookie Settings
                  </h2>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    We use cookies to improve your experience, analyze site
                    traffic, and for marketing purposes.{' '}
                    <a
                      href="/cookies"
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  variant="primary"
                  onClick={acceptAll}
                  className="flex-1"
                >
                  Accept All
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPreferences(true)}
                  className="flex-1"
                >
                  Customize
                </Button>
                <Button
                  variant="ghost"
                  onClick={rejectAll}
                  className="flex-1 text-slate-400"
                >
                  Reject All
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">
                  Cookie Preferences
                </h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-start justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium text-white">
                      Necessary Cookies
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Required for the site to function.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="w-5 h-5 rounded"
                  />
                </div>
                <div className="flex items-start justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium text-white">
                      Analytics Cookies
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Help us understand how visitors use the site.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        analytics: e.target.checked,
                      }))
                    }
                    className="w-5 h-5 rounded cursor-pointer"
                  />
                </div>
                <div className="flex items-start justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium text-white">
                      Marketing Cookies
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Used to show relevant ads.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        marketing: e.target.checked,
                      }))
                    }
                    className="w-5 h-5 rounded cursor-pointer"
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  variant="primary"
                  onClick={acceptSelected}
                  className="flex-1"
                >
                  Save Preferences
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowPreferences(false)}
                  className="text-slate-400"
                >
                  Back
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/50 rounded-b-xl">
          <div className="flex justify-center gap-4 text-xs text-slate-500">
            <a href="/privacy" className="hover:text-slate-300">
              Privacy Policy
            </a>
            <span>•</span>
            <a href="/terms" className="hover:text-slate-300">
              Terms of Service
            </a>
            <span>•</span>
            <a href="/cookies" className="hover:text-slate-300">
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
